import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getImageUrl } from '../../utils/api';
import Loader from '../../components/common/Loader';
import '../../styles/Forms.css';

const AcknowledgePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [handover, setHandover] = useState(null);
  const [formData, setFormData] = useState({
    received: false,
    inspected: false,
    responsible: false,
    comments: '',
    issues: '',
    reportIssue: false,
  });

  useEffect(() => {
    fetchHandover();
  }, [token]);

  const fetchHandover = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/acknowledge/${token}`);
      const data = await response.json();

      if (data.success) {
        setHandover(data.data);
      } else {
        toast.error(data.message || 'Invalid or expired acknowledgment link');
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    } catch (error) {
      console.error('Error fetching handover:', error);
      toast.error('Error loading acknowledgment details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If reporting issue, only validate issues field
    if (formData.reportIssue) {
      if (!formData.issues.trim()) {
        toast.error('Please describe the issue');
        return;
      }
    } else {
      // For normal acknowledgment, validate all checkboxes are checked
      if (!formData.received || !formData.inspected || !formData.responsible) {
        toast.error('Please confirm all checklist items');
        return;
      }
    }

    try {
      setSubmitting(true);
      
      // Prepare submission data
      const submitData = {
        received: formData.received,
        inspected: formData.inspected,
        comments: formData.comments || undefined,
        issues: formData.reportIssue ? formData.issues : undefined,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/acknowledge/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (data.success) {
        if (formData.reportIssue) {
          toast.success('Issue reported successfully! Admin will be notified.');
        } else {
          toast.success('Asset acknowledged successfully!');
        }
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        toast.error(data.message || 'Error acknowledging asset');
      }
    } catch (error) {
      console.error('Error acknowledging asset:', error);
      toast.error('Error acknowledging asset');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!handover) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Invalid Link</h2>
        <p>The acknowledgment link is invalid or has expired.</p>
      </div>
    );
  }

  const asset = handover.asset;
  const assignedTo = handover.assignedTo;

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f7fafc', 
      padding: '20px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{
        background: 'white',
        padding: '32px',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        maxWidth: '700px',
        width: '100%'
      }}>
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600, color: '#2d3748' }}>
            Acknowledge Asset Receipt
          </h1>
          <p style={{ margin: 0, color: '#718096', fontSize: '14px' }}>
            Please confirm that you have received and inspected the asset
          </p>
        </div>

        {/* Asset Details */}
        <div style={{
          background: '#f7fafc',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>
            Asset Details
          </h3>
          
          {asset.photos && asset.photos.length > 0 && (
            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
              <img
                src={getImageUrl(asset.photos[0].url)}
                alt={asset.description}
                style={{
                  maxWidth: '200px',
                  maxHeight: '200px',
                  borderRadius: '8px',
                  objectFit: 'contain'
                }}
              />
            </div>
          )}

          <div style={{ display: 'grid', gap: '12px' }}>
            <div>
              <strong>Asset Tag:</strong> {asset.assetTag || 'N/A'}
            </div>
            <div>
              <strong>Description:</strong> {asset.description || 'N/A'}
            </div>
            {asset.brand && (
              <div>
                <strong>Brand:</strong> {asset.brand}
              </div>
            )}
            {asset.model && (
              <div>
                <strong>Model:</strong> {asset.model}
              </div>
            )}
            {asset.serialNumber && (
              <div>
                <strong>Serial Number:</strong> {asset.serialNumber}
              </div>
            )}
            {asset.condition && (
              <div>
                <strong>Condition:</strong> {asset.condition}
              </div>
            )}
          </div>
        </div>

        {/* Assignment Info */}
        <div style={{
          background: '#f7fafc',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>
            Assignment Information
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div>
              <strong>Assigned To:</strong> {assignedTo.name || 'N/A'} ({assignedTo.email || 'N/A'})
            </div>
            <div>
              <strong>Assigned Date:</strong> {new Date(handover.assignedDate).toLocaleDateString()}
            </div>
            {handover.expectedReturnDate && (
              <div>
                <strong>Expected Return Date:</strong> {new Date(handover.expectedReturnDate).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* Acknowledgment Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>
              Confirm Receipt
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                padding: '10px',
                background: formData.received ? '#c6f6d5' : '#f7fafc',
                border: `2px solid ${formData.received ? '#38a169' : '#e2e8f0'}`,
                borderRadius: '6px',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  name="received"
                  checked={formData.received}
                  onChange={handleInputChange}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: '13px', fontWeight: 500, color: formData.received ? '#22543d' : '#4a5568' }}>
                  I have physically received all the selected assets.
                </span>
              </label>
              
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                padding: '10px',
                background: formData.inspected ? '#c6f6d5' : '#f7fafc',
                border: `2px solid ${formData.inspected ? '#38a169' : '#e2e8f0'}`,
                borderRadius: '6px',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  name="inspected"
                  checked={formData.inspected}
                  onChange={handleInputChange}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: '13px', fontWeight: 500, color: formData.inspected ? '#22543d' : '#4a5568' }}>
                  I have inspected the condition of the assets.
                </span>
              </label>
              
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                padding: '10px',
                background: formData.responsible ? '#c6f6d5' : '#f7fafc',
                border: `2px solid ${formData.responsible ? '#38a169' : '#e2e8f0'}`,
                borderRadius: '6px',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  name="responsible"
                  checked={formData.responsible}
                  onChange={handleInputChange}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: '13px', fontWeight: 500, color: formData.responsible ? '#22543d' : '#4a5568' }}>
                  I agree that these assets are under my responsibility.
                </span>
              </label>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="comments">Comments (Optional)</label>
            <textarea
              id="comments"
              name="comments"
              value={formData.comments}
              onChange={handleInputChange}
              rows="4"
              placeholder="Any additional comments about the asset..."
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '10px',
              background: formData.reportIssue ? '#fed7d7' : '#f7fafc',
              border: `2px solid ${formData.reportIssue ? '#e53e3e' : '#e2e8f0'}`,
              borderRadius: '6px',
              transition: 'all 0.2s'
            }}>
              <input
                type="checkbox"
                name="reportIssue"
                checked={formData.reportIssue}
                onChange={handleInputChange}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '13px', fontWeight: 500, color: formData.reportIssue ? '#742a2a' : '#4a5568' }}>
                Report Issue
              </span>
            </label>
          </div>

          {formData.reportIssue && (
            <div className="form-group" style={{ marginBottom: '24px' }}>
              <label htmlFor="issues">Describe the Issue *</label>
              <textarea
                id="issues"
                name="issues"
                value={formData.issues}
                onChange={handleInputChange}
                rows="4"
                placeholder="Please describe the issue or damage found..."
                required
              />
              <small style={{ color: '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                If you report issues, the asset status will be set to "Issue Reported"
              </small>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              disabled={
                submitting || 
                (
                  formData.reportIssue 
                    ? !formData.issues.trim()  // If reporting issue, only need issues field
                    : (!formData.received || !formData.inspected || !formData.responsible)  // Otherwise need all checkboxes
                )
              }
              style={{
                padding: '12px 24px',
                background: (
                  formData.reportIssue 
                    ? formData.issues.trim()  // If reporting issue, enable when issues filled
                    : (formData.received && formData.inspected && formData.responsible)  // Otherwise need all checkboxes
                )
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : '#cbd5e0',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: (
                  formData.reportIssue 
                    ? (formData.issues.trim() && !submitting)
                    : (formData.received && formData.inspected && formData.responsible && !submitting)
                ) ? 'pointer' : 'not-allowed',
                fontWeight: 600,
                fontSize: '14px',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? 'Submitting...' : formData.reportIssue ? 'Report Issue' : 'Acknowledge Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcknowledgePage;

